import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, isFirebaseAuthAvailable } from '../firebase';

function Login() {
  const navigate = useNavigate();
  const { currentUser, mockLogin, loginWithToken } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to dashboard
  if (currentUser) {
    return <Navigate to="/dashboard" />;
  }

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const { idToken } = await signInWithGoogle();
      await loginWithToken(idToken);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Firebase Auth. Add it in Firebase Console > Authentication > Settings.');
      } else {
        setError(err.message || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const firebaseAvailable = isFirebaseAuthAvailable();

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#121212',
      color: 'white',
      fontFamily: 'sans-serif'
    }}>
      <div style={{
        backgroundColor: '#1e1e1e',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
        textAlign: 'center',
        maxWidth: 400,
        width: '100%',
      }}>
        <div style={{ marginBottom: '24px' }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <h1 style={{ marginBottom: '8px', fontSize: '28px', fontWeight: 700 }}>Dare2Chat</h1>
        <p style={{ marginBottom: '32px', color: '#888', fontSize: '14px' }}>Anonymous chat with Fr</p>

        {/* Google Sign-In Button */}
        {firebaseAvailable ? (
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #333',
              background: loading ? '#2a2a2a' : '#fff',
              color: loading ? '#666' : '#333',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
            </svg>
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </button>
        ) : (
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            background: '#2a2a2a',
            marginBottom: '16px',
          }}>
            <p style={{ fontSize: '13px', color: '#fbbf24', margin: 0 }}>
              ⚠️ Firebase Auth not configured
            </p>
            <p style={{ fontSize: '12px', color: '#888', marginTop: '8px', marginBottom: 0 }}>
              Set REACT_APP_FIREBASE_* environment variables in your .env file.
            </p>
          </div>
        )}

        {error && (
          <p style={{ color: '#fca5a5', fontSize: 13, marginTop: '16px', marginBottom: 0, padding: '8px', background: 'rgba(252,165,165,0.1)', borderRadius: '4px' }}>
            {error}
          </p>
        )}

        {/* Continue as Guest - for users who joined via invite links */}
        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #333' }}>
          <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
            👤 No account? No problem.
          </p>
          <button
            onClick={() => navigate('/guest')}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(139,92,246,0.3)',
              background: 'rgba(139,92,246,0.1)',
              color: '#8b5cf6',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              fontFamily: 'inherit',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            Continue as Guest
          </button>
        </div>

        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #333' }}>
          <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
            🔧 Development Mode
          </p>
          <button
            onClick={mockLogin}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#2a5a2a' : '#4CAF50',
              color: 'white',
              padding: '10px 15px',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              width: '100%',
              fontSize: '13px',
              opacity: loading ? 0.6 : 1,
            }}
          >
            Enter App (Local Mode)
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
